import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import useCruiseColumn from 'components/elements/table/editable-columns/cruise-column';
import TableEditableComponent from 'components/elements/table/table';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetCruiseShips from 'hooks/use-get-cruise-ships';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { CruiseSaleFormType, CruiseSaleCruiseSaleFormType } from '../form-type';

interface Props {
  parentIndex: number;
}

const name = 'cruiseSales' as const;

function useGetCruiseTableList(
  parentIndex,
): IColumn<CruiseSaleCruiseSaleFormType>[] {
  const { t } = useTranslation();
  const cruiseColumn = useCruiseColumn();
  const cruiseIdName = `${name}.${parentIndex}.cruiseId`;
  const { cruiseShips } = useGetCruiseShips(cruiseIdName);
  const cruiseId = useWatch({
    name: cruiseIdName,
  });

  const columns = React.useMemo(() => {
    return [
      {
        header: t('common:cruise_line'),
        accessorKey: 'cruiseId',
        minSize: 180,
        maxSize: 200,
        dataType: ColumnDataType.select,
        required: true,
        selectProps: cruiseColumn,
      },
      {
        header: t('cruise:ship'),
        accessorKey: 'cruiseShipId',
        minSize: 180,
        maxSize: 200,
        dataType: ColumnDataType.select,
        required: true,
        options: cruiseShips,
        disabled: !cruiseId,
      },
      {
        header: t('common:from'),
        accessorKey: 'startedAt',
        minSize: 150,
        maxSize: 150,
        required: true,
        dataType: ColumnDataType.date,
      },
      {
        header: t('common:to'),
        accessorKey: 'endedAt',
        minSize: 150,
        maxSize: 150,
        required: true,
        dataType: ColumnDataType.date,
      },
      {
        header: t('common:departure'),
        accessorKey: 'startedAt',
        minSize: 100,
        maxSize: 150,
        required: true,
        dataType: ColumnDataType.time,
      },
      {
        header: t('common:arrival'),
        accessorKey: 'endedAt',
        minSize: 100,
        maxSize: 150,
        required: true,
        dataType: ColumnDataType.time,
      },
      {
        header: t('common:route'),
        accessorKey: 'route',
        minSize: 200,
        required: true,
        dataType: ColumnDataType.text,
      },
    ];
  }, [cruiseColumn, cruiseId, cruiseShips, t]);
  return columns;
}

export default function CruiseTable(props: Props) {
  const { editable } = useFormState();

  const { control, getValues, clearErrors } =
    useFormContext<CruiseSaleFormType>();

  const fields = getValues(name);

  const { update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetCruiseTableList(props.parentIndex);

  const dispatch = React.useCallback(
    (action) => {
      const columnId = action.columnId as keyof CruiseSaleCruiseSaleFormType;
      const extra = action.extra as Value;
      const currentRowIndex = props.parentIndex;
      const cruises = getValues(name);
      const cruise = cruises[currentRowIndex];
      const value = action.value;

      const type = action.type as ActionType;

      const otherVal: () => Partial<CruiseSaleCruiseSaleFormType> = () => {
        if (columnId === 'cruiseId') {
          const cruiseName = extra?.[0]?.extra.name;
          return {
            cruiseName,
          };
        }
        if (columnId === 'cruiseShipId') {
          return {
            cruiseShipName: extra?.[0]?.label as string,
          };
        }
        return {};
      };

      if (type === 'update_cell') {
        update(currentRowIndex, {
          ...cruise,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${props.parentIndex}.${columnId}`);
      }
    },
    [clearErrors, getValues, props.parentIndex, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableEditableComponent
        columns={columns}
        data={[fields[props.parentIndex]]}
        dispatch={dispatch}
        disabled={!editable}
        noAddRemoveRow
        uniqueRowKey="customId"
      />
    </div>
  );
}
